import type { SchemaInput } from './schema-input.js';
import { isNumber, isSchemaRecord, isString } from './runtime-values.js';

export const SOURCE_ATTRIBUTION_STORAGE_KEY = 'drawonamap:source-attribution:v1';
export const SOURCE_ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000;

export interface ExternalSource {
  referrer_host: string | null;
  landing_page: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  captured_at: number;
}

export interface SourceAttribution {
  version: 1;
  first: ExternalSource | null;
  last: ExternalSource | null;
}

export function cleanCampaignTag(value: SchemaInput): string | null {
  return isString(value) && /^[a-z0-9][a-z0-9._-]{0,99}$/i.test(value) ? value.toLowerCase() : null;
}

export function isAttributionTransitHost(host: string): boolean {
  return /^(?:www\.)?drawonamap\.com$/.test(host)
    || /^accounts\.google\.[a-z.]+$/.test(host)
    || /^(?:checkout|billing)\.stripe\.com$/.test(host);
}

export function cleanAttributionPath(path: string): string {
  if (/^\/(?:[a-z]{2}\/)?(?:m|embed)\//.test(path)) return '/shared-map/';
  return /^\/[a-z0-9/_-]{0,199}$/i.test(path) ? path.toLowerCase() : '/other/';
}

function parseExternalSource(value: SchemaInput, now: number): ExternalSource | null {
  if (!isSchemaRecord(value) || !isNumber(value.captured_at)
    || !Number.isSafeInteger(value.captured_at) || value.captured_at > now
    || value.captured_at <= now - SOURCE_ATTRIBUTION_TTL_MS
    || !isString(value.landing_page) || cleanAttributionPath(value.landing_page) !== value.landing_page) return null;
  const host = value.referrer_host;
  if (host !== null && (!isString(host) || !/^[a-z0-9.-]{1,120}$/.test(host)
    || !host.includes('.') || isAttributionTransitHost(host))) return null;
  const utmSource = cleanCampaignTag(value.utm_source);
  const utmMedium = cleanCampaignTag(value.utm_medium);
  const utmCampaign = cleanCampaignTag(value.utm_campaign);
  if (value.utm_source !== utmSource || value.utm_medium !== utmMedium || value.utm_campaign !== utmCampaign) return null;
  if (!host && !utmSource && !utmMedium && !utmCampaign) return null;
  return { referrer_host: host, landing_page: value.landing_page, utm_source: utmSource,
    utm_medium: utmMedium, utm_campaign: utmCampaign, captured_at: value.captured_at };
}

export function parseSourceAttribution(value: SchemaInput, now = Date.now()): SourceAttribution | null {
  if (!isSchemaRecord(value) || value.version !== 1) return null;
  if (value.first === null && value.last === null) return { version: 1, first: null, last: null };
  const first = parseExternalSource(value.first, now);
  const last = parseExternalSource(value.last, now);
  if (!first || !last || first.captured_at > last.captured_at) return null;
  return { version: 1, first, last };
}

export function parseSourceAttributionJson(value: SchemaInput, now = Date.now()): SourceAttribution | null {
  if (!isString(value) || value.length > 2000) return null;
  try { return parseSourceAttribution(JSON.parse(value), now); } catch { return null; }
}
